import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import Sidebar from '@/components/Sidebar';
import StatCard from '@/components/StatCard';
import AssessmentCard, { Assessment } from '@/components/AssessmentCard';
import CandidateCard, { Candidate } from '@/components/CandidateCard';
import { FileText, Users, CheckCircle, Clock, Plus, CreditCard, ChevronRight, InfoIcon, Mic, Brain, ArrowRight, Calendar, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase, ENABLE_REALTIME } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { getUserCredits } from '@/services/creditService';
import { useSelectCandidate } from '@/hooks/useSelectCandidate';
import CandidateSheet from '@/components/candidate/CandidateSheet';
import { RealtimeChannel } from '@supabase/supabase-js';
import { Alert, AlertDescription } from "@/components/ui/alert";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, profile, isCandidate, isRecruiter, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAssessments: 0,
    candidatesInvited: 0,
    assessmentsCompleted: 0,
    credits: 0
  });
  const [recentAssessments, setRecentAssessments] = useState<Assessment[]>([]);
  const [recentCandidates, setRecentCandidates] = useState<Candidate[]>([]);
  const [subscriptions, setSubscriptions] = useState<RealtimeChannel[]>([]);
  const lastFetchedRef = useRef<number>(0);
  const REFETCH_THRESHOLD = 30000;

  const {
    selectedCandidate,
    candidateAssessment,
    handleCandidateClick,
    handleCloseCandidate
  } = useSelectCandidate();

  const setupRealtimeSubscriptions = useCallback(() => {
    if (!user) return;
    
    // Check if realtime is enabled
    if (!ENABLE_REALTIME) {
      // console.log('Realtime subscriptions disabled via ENABLE_REALTIME flag');
      return () => {};
    }
    
    // Original realtime subscription code
    // Subscribe to assessments changes
    const assessmentsSubscription = supabase
      .channel('assessments-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'assessments',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // console.log('Assessment change detected, refreshing data...');
          fetchDashboardData();
        }
      )
      .subscribe();

    // Subscribe to candidates changes
    const candidatesSubscription = supabase
      .channel('candidates-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'candidates'
        },
        () => {
          // console.log('Candidate change detected, refreshing data...');
          fetchDashboardData();
        }
      )
      .subscribe();

    // Subscribe to user_credits changes
    const creditsSubscription = supabase
      .channel('credits-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_credits',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // console.log('Credits change detected, refreshing data...');
          fetchDashboardData();
        }
      )
      .subscribe();

    setSubscriptions([
      assessmentsSubscription,
      candidatesSubscription,
      creditsSubscription
    ]);

    return () => {
      assessmentsSubscription.unsubscribe();
      candidatesSubscription.unsubscribe();
      creditsSubscription.unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (user && isRecruiter()) {
      fetchDashboardData();
      const cleanup = setupRealtimeSubscriptions();
      return () => {
        cleanup?.();
        subscriptions.forEach(subscription => subscription.unsubscribe());
      };
    }
  }, [user, isRecruiter, setupRealtimeSubscriptions]);

  // Set up an interval to check connection status and resubscribe if needed
  useEffect(() => {
    if (!user) return;
    
    // Add visibility change handler with data freshness check
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        // Only refetch if data is older than the threshold
        if (now - lastFetchedRef.current > REFETCH_THRESHOLD) {
          fetchDashboardData();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const fetchDashboardData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch user credits
      const userCredits = await getUserCredits(user.id);
      // console.log("User credits fetched:", userCredits); // Add logging for debugging
      
      // Get subscription details to show proper credit information
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select(`
          *,
          plans (
            credits
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();
      
      if (subscriptionError && subscriptionError.code !== 'PGRST116') {
        console.error('Error fetching subscription:', subscriptionError);
      }
      
      const planCredits = subscriptionData?.plans?.credits || 0;
      // console.log("Plan credits:", planCredits); // Add logging for debugging

      // Fetch total assessments count
      const { count: totalAssessmentsCount, error: countError } = await supabase
        .from('assessments')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (countError) {
        console.error('Error fetching total assessments count:', countError);
      }

      // Fetch recent assessments
      const { data: assessmentsData, error: assessmentsError } = await supabase
        .from('assessments')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (assessmentsError) {
        console.error('Error fetching assessments:', assessmentsError);
        // Continue with empty assessments data
      }

      // Initialize variables with safe defaults
      let candidatesData: any[] = [];
      let totalAssessments = totalAssessmentsCount || 0;
      let candidatesInvited = 0;
      let assessmentsCompleted = 0;
      let formattedCandidates: Candidate[] = [];

      // Only calculate these if we have assessment data
      if (assessmentsData && assessmentsData.length > 0) {
        candidatesInvited = assessmentsData.reduce((sum, assessment) => sum + (assessment.candidates_invited || 0), 0);
      }

      // Try to fetch candidates, but handle errors gracefully
      try {
        // First get all assessments for the current user
        const { data: userAssessments, error: assessmentsError } = await supabase
          .from('assessments')
          .select('id')
          .eq('user_id', user.id);
        
        if (assessmentsError) {
          console.error('Error fetching user assessments:', assessmentsError);
          // Continue with empty candidates data
        } else if (userAssessments && userAssessments.length > 0) {
          // Get the assessment IDs
          const assessmentIds = userAssessments.map(a => a.id);
          
          // Then get candidates for those assessments
          const { data: candidates, error: candidatesError } = await supabase
            .from('candidates')
            .select(`
              *,
              assessments (
                title
              )
            `)
            .in('assessment_id', assessmentIds)
            .order('created_at', { ascending: false });

          if (candidatesError) {
            console.error('Error fetching candidates:', candidatesError);
          } else {
            candidatesData = candidates || [];
            // Calculate total completed candidates
            assessmentsCompleted = candidatesData.filter(c => c.status === 'completed').length;
          }

          // Get recent candidates (limited to 4)
          const { data: recentCandidatesData, error: recentCandidatesError } = await supabase
            .from('candidates')
            .select(`
              *,
              assessments (
                title
              )
            `)
            .in('assessment_id', assessmentIds)
            .order('created_at', { ascending: false })
            .limit(4);

          if (recentCandidatesError) {
            console.error('Error fetching recent candidates:', recentCandidatesError);
          }

          // Format candidates for display (using limited recent candidates)
          formattedCandidates = (recentCandidatesData || []).map(candidate => ({
            id: candidate.id,
            name: candidate.name,
            email: candidate.email,
            assessmentTitle: candidate.assessments?.title || '',
            status: candidate.status as 'invited' | 'in_progress' | 'completed',
            completedDate: candidate.completed_date,
            score: candidate.score,
            aiRating: candidate.ai_rating,
            potentialPlagiarism: candidate.potential_plagiarism,
            skill_ratings: candidate.skill_ratings,
            ai_summary: candidate.ai_summary
          }));
        }
      } catch (candidateError) {
        console.error('Exception fetching candidates:', candidateError);
      }

      // Format assessments for display
      const formattedAssessments: Assessment[] = (assessmentsData || []).map(assessment => {
        // Get candidates for this assessment
        const assessmentCandidates = candidatesData.filter(c => c.assessment_id === assessment.id);
        const completedCount = assessmentCandidates.filter(c => c.status === 'completed').length;
        const inProgressCount = assessmentCandidates.filter(c => c.status === 'in_progress').length;
        const invitedCount = assessmentCandidates.filter(c => c.status === 'invited').length;
        const totalCount = invitedCount + inProgressCount + completedCount; // Total is sum of all candidates

        return {
          id: assessment.id,
          title: assessment.title,
          skills: assessment.skills || [],
          candidatesInvited: totalCount,
          candidatesCompleted: completedCount,
          candidatesInProgress: inProgressCount,
          date: new Date(assessment.created_at).toLocaleDateString(),
          status: assessment.status as 'draft' | 'active' | 'completed' | 'inactive'
        };
      });

      // Update stats with the correct counts
      const totalInvited = (assessmentsData || []).reduce((sum, assessment) => {
        const assessmentCandidates = candidatesData.filter(c => c.assessment_id === assessment.id);
        const invitedCount = assessmentCandidates.filter(c => c.status === 'invited').length;
        const inProgressCount = assessmentCandidates.filter(c => c.status === 'in_progress').length;
        const completedCount = assessmentCandidates.filter(c => c.status === 'completed').length;
        // console.log(`Assessment ${assessment.title}: ${invitedCount} invited, ${inProgressCount} in progress, ${completedCount} completed`);
        return sum + invitedCount + inProgressCount + completedCount;
      }, 0);

      // console.log(`Total candidates across all assessments: ${totalInvited}`);

      setStats({
        totalAssessments,
        candidatesInvited: totalInvited,
        assessmentsCompleted,
        credits: userCredits?.balance || 0
      });
      
      // console.log("Setting stats with credits:", userCredits?.balance || 0); // Add logging for debugging

      setRecentAssessments(formattedAssessments.slice(0, 3)); // Only show 3 most recent assessments in UI
      setRecentCandidates(formattedCandidates);

      // At the end of successful data fetching:
      lastFetchedRef.current = Date.now();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Fall back to showing 0 credits if there was an error
      setStats(prevStats => ({
        ...prevStats,
        credits: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        {selectedCandidate && (
          <CandidateSheet
            open={true}
            onOpenChange={(open) => !open && handleCloseCandidate()}
            candidate={selectedCandidate}
            candidateAssessment={candidateAssessment}
            onRefresh={fetchDashboardData}
          />
        )}
        
        <main className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto w-full">
          <div id="dashboard-header" className="flex items-center justify-between mb-8 dashboard-overview">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Dashboard</h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Welcome back to your RoundOne dashboard.</p>
            </div>
            <Button 
              id="create-assessment-btn"
              onClick={() => navigate('/assessments/create')}
              size="lg" 
              className="create-assessment-button gradient-button"
            >
              <Plus className="mr-2 h-5 w-5" /> Create Assessment
            </Button>
          </div>
          
          {/* Migration Notice */}
          {/* Alert removed as requested */}
          
          {/* Stats */}
          <div className="grid gap-6 mb-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Assessments"
              value={stats.totalAssessments}
              icon={<FileText className="h-5 w-5 text-blue-600" />}
              description="All time"
            />
            <StatCard
              title="Candidates Invited"
              value={stats.candidatesInvited}
              icon={<Users className="h-5 w-5 text-emerald-600" />}
              description="All time"
            />
            <StatCard
              title="Assessments Completed"
              value={stats.assessmentsCompleted}
              icon={<CheckCircle className="h-5 w-5 text-purple-600" />}
              description="All time"
            />
            <StatCard
              title="Credits Available"
              value={stats.credits}
              icon={<CreditCard className="h-5 w-5 text-amber-600" />}
              description="Available to use"
              onClick={() => navigate('/credits')}
            />
          </div>
          
          {/* Recent Assessments and Candidates */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Recent Assessments */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle>Recent Assessments</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/assessments')}>
                    View all <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pb-1">
                {loading ? (
                  <div className="flex justify-center items-center h-40">
                    <div className="w-10 h-10 border-4 border-roundone-purple border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : recentAssessments.length > 0 ? (
                  <div className="space-y-4">
                    {recentAssessments.map(assessment => (
                      <AssessmentCard
                        key={assessment.id}
                        assessment={assessment}
                        onClick={() => navigate(`/assessments/${assessment.id}`)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <h3 className="text-gray-500 font-medium mb-1">No assessments yet</h3>
                    <p className="text-gray-400 text-sm mb-4">Create your first assessment to get started</p>
                    <Button 
                      size="sm" 
                      onClick={() => navigate('/assessments/create')}
                      className="gradient-button"
                    >
                      <Plus className="mr-2 h-4 w-4" /> Create Assessment
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Recent Candidates */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle>Recent Candidates</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/candidates')}>
                    View all <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pb-1">
                {loading ? (
                  <div className="flex justify-center items-center h-40">
                    <div className="w-10 h-10 border-4 border-roundone-purple border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : recentCandidates.length > 0 ? (
                  <div className="space-y-4">
                    {recentCandidates.map(candidate => (
                      <CandidateCard
                        key={candidate.id}
                        candidate={candidate}
                        onClick={(id) => handleCandidateClick(id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <h3 className="text-gray-500 font-medium mb-1">No candidates yet</h3>
                    <p className="text-gray-400 text-sm mb-4">Invite candidates to your assessments</p>
                    <Button size="sm" onClick={() => navigate('/assessments')}>
                      <Users className="mr-2 h-4 w-4" /> Manage Candidates
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
